/**
 * UForm text tokenization (ticket 07). UForm uses a SentencePiece **Unigram**
 * tokenizer (Metaspace pre-tokenizer, `<s>`/`</s>`/`<unk>`/`<pad>` special
 * tokens — see the vendored `tokenizer.json`). This wraps
 * `@huggingface/tokenizers` (a pure-JS tokenizer, no native/WASM addon) so the
 * *same* byte-level encoding runs identically in Node and the browser, and
 * constructs it directly from the tokenizer JSON (no Hub/network access).
 */
import { Tokenizer } from "@huggingface/tokenizers";
import { MAX_SEQUENCE_LENGTH, PAD_TOKEN_ID } from "./config.js";
/**
 * Build a tokenizer from the parsed `tokenizer.json` content. Returns a sync
 * function that tokenizes one text, adding the `<s>`/`</s>` special tokens and
 * padding to `MAX_SEQUENCE_LENGTH` with the `<pad>` id (matching the SDK's
 * `TextProcessor.process`: `add_special_tokens`, `padding: 'max_length'`,
 * `truncation`).
 */
export function createTextTokenizer(tokenizerJson) {
    const tokenizer = new Tokenizer(tokenizerJson, {});
    return (text) => {
        const { ids, attention_mask: attentionMask } = tokenizer.encode(text, {
            add_special_tokens: true,
        });
        return {
            inputIds: toFixedLength(ids, MAX_SEQUENCE_LENGTH, PAD_TOKEN_ID, true),
            attentionMask: toFixedLength(attentionMask, MAX_SEQUENCE_LENGTH, 0, false),
        };
    };
}
/**
 * Pad `values` on the right (or truncate) to exactly `length` entries. When
 * `preserveLast` is set, truncation keeps the trailing special token (the
 * `</s>`) instead of slicing it off — matching the tokenizer's `truncation`
 * behaviour, which always retains the sentence boundary token.
 */
function toFixedLength(values, length, padValue, preserveLast) {
    if (values.length > length) {
        if (preserveLast)
            return [...values.slice(0, length - 1), values[values.length - 1]];
        return values.slice(0, length);
    }
    if (values.length < length) {
        const out = new Array(length).fill(padValue);
        out.splice(0, values.length, ...values);
        return out;
    }
    return values;
}
//# sourceMappingURL=tokenizer.js.map